import "react-date-range/dist/styles.css" // main style file
import "react-date-range/dist/theme/default.css" // theme css file

import {
  useEffect,
  useRef,
  useState,
  FocusEvent,
  KeyboardEvent,
  PointerEvent,
  MouseEvent as ReactMouseEvent,
  useMemo,
} from "react"

import {
  addLocaleTzOffset,
  range_display_format_obj,
  removeLocaleTzOffset,
} from "utils/dates"
import { css, cx } from "@emotion/css"
import { endOfDay, startOfDay } from "date-fns"

import { Button } from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ReactComponent as CalendarIcon } from "assets/campaign/calendar.svg"
import { ReactComponent as IconCalendar } from "assets/icon-calendar.svg"
import { InputBaseProps } from "@material-ui/core/InputBase"
import Popover from "@material-ui/core/Popover"
import { DateRange as ReactDateRange } from "react-date-range"
import TextField from "@material-ui/core/TextField"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { utcToZonedTime } from "date-fns-tz"

const DATE_RANGE_YEARS_PAST = 5
const DATE_RANGE_YEARS_FUTURE = 10

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    width: fit-content;
    border-radius: 4px;
    --color: inherit;
    --border-color: transparent;
    /* --border-color: rgba(0, 0, 0, 0.38); */
    :hover {
      cursor: pointer;
      --border-color: var(--primary-main);
    }
  `,
  fullWidth: css`
    width: 100%;
  `,
  disabled: css`
    --color: rgba(0, 0, 0, 0.38);
    :hover {
      cursor: default;
      --border-color: transparent;
    }
  `,
  pair: css`
    width: 100%;
    display: inline-flex;
    align-items: center;
    border-width: 1px;
    border-style: dashed;
    border-color: var(--border-color);
    border-radius: 4px;
    display: inline-flex;
    transition: border-color 200ms ease-in-out;
    :focus-within {
      border-color: var(--primary-main);
    }
  `,
  inputBaseClasses: {
    root: css`
      width: 100%;
      min-width: 6ch;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      letter-spacing: inherit;
    `,
    input: css`
      padding: 6px;
      user-select: none;
      :enabled {
        cursor: pointer;
      }
    `,
  },
  sep: css`
    margin: 0 4px;
    color: var(--color);
  `,
  error: css`
    margin-top: 4px;
  `,

  variants: {
    smaller: css`
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.1876em;
      letter-spacing: 0.00938em;
    `,
    bolder: css`
      font-size: 1rem;
      line-height: 1.6;
      font-weight: 500;
    `,
  },
  input: css`
    width: 100%;
  `,
  focused: css`
    border: 2px solid var(--primary-main) !important;
  `,
  focusedError: css`
    border: 2px solid var(--red);
  `,
  label: css`
    &.MuiInputLabel-outlined.MuiInputLabel-marginDense {
      transform: translate(38px, 10px) scale(1);
    }
    &.MuiInputLabel-shrink {
      transform: translate(13px, -6px) scale(0.75) !important;
    }
  `,
  labelShrink: css`
    color: var(--primary-main);
    transform: translate(13px, -6px) scale(0.75) !important;
  `,
  labelBlurShrink: css`
    transform: translate(13px, -6px) scale(0.75) !important;
  `,
  calendarIcon: css`
    > g {
      path {
        fill: var(--dark-grey);
      }
      > g > path {
        fill: var(--dark-grey);
      }
    }
  `,
  startAdornment: css`
    padding-left: var(--spacing-m);
  `,
  outlinedInput: css`
    padding-left: var(--spacing-xs) !important;
    min-width: 22.5ch;
    padding: 0;
  `,
}

const min_date = new Date()
const max_date = new Date()

export const parseDate = (
  date: Date | number | string | null | undefined,
  timeZone: string,
  locale: Locale,
): Date | null => {
  if (date === null || date === undefined) {
    return null
  } else {
    return utcToZonedTime(date, timeZone, { locale })
  }
}

type Range = {
  startDate: Date | null
  endDate: Date | null
  key: string
}

export type DateRangeAgainProps = {
  disabled?: boolean
  start?: Date | number | string | null
  end?: Date | number | string | null
  onRangeChange: (start: Date | null, end: Date | null) => void
  onRangeClear?: () => void
  //
  minDate?: Date | number | string | null
  maxDate?: Date | number | string | null
  //
  name?: string
  placeholder?: string
  //
  onFocus?: InputBaseProps["onFocus"]
  onBlur?: InputBaseProps["onBlur"]
  //
  label?: string
  submitLabel?: string
  clearLabel?: string
  error?: string
  //
  fullWidth?: boolean
  className?: string
  labelClassName?: string
  inputClassName?: string
  notchedClasName?: string
  hasStartAdornment?: boolean
  //
  variant?: "bolder" | "smaller"
  margin?: "none" | "dense" | "normal"
  size?: "small" | "medium"
}
export function DateRangeAgain(props: DateRangeAgainProps) {
  const {
    disabled = false,
    start,
    end,
    onRangeChange,
    onRangeClear,
    //
    minDate,
    maxDate,

    placeholder,
    //
    onFocus,
    onBlur,
    //
    label,
    submitLabel = "apply",
    clearLabel = "cancel",
    error,
    //
    fullWidth = false,
    className,
    labelClassName,
    inputClassName,
    notchedClasName,
    hasStartAdornment,
    variant = "smaller",
    margin = "none",
    size = "small",
  } = props
  const { t, dateLocale } = useI18n()
  const { meta, dateFormatter } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}

  const date_now = utcToZonedTime(new Date(), timeZone)
  const start_of_day = startOfDay(date_now)
  const end_of_day = endOfDay(date_now)
  min_date.setFullYear(date_now.getFullYear() - DATE_RANGE_YEARS_PAST)
  max_date.setFullYear(date_now.getFullYear() + DATE_RANGE_YEARS_FUTURE)

  const pairRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState<boolean>(() => false)

  const _start =
    typeof start !== "string" && typeof start !== "number"
      ? removeLocaleTzOffset(start)?.toISOString()
      : start
  const _end =
    typeof end !== "string" && typeof end !== "number"
      ? removeLocaleTzOffset(end)?.toISOString()
      : end

  const startDate = parseDate(_start, timeZone, dateLocale)
  const startInputValue = dateFormatter(_start, range_display_format_obj) || ""

  const endDate = parseDate(_end, timeZone, dateLocale)
  const endInputValue = dateFormatter(_end, range_display_format_obj) || ""
  const inputValue = start && end ? startInputValue + " - " + endInputValue : ""

  const reset = () => [
    {
      key: "selection",
      startDate: startDate || start_of_day,
      endDate: endDate || end_of_day,
    },
  ]

  const [ranges, setRanges] = useState<Array<Range>>(reset)

  useEffect(
    () => setRanges(reset),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [start, end],
  )

  const handleChange = (item: any) => {
    setRanges([item.selection])
  }

  const handleFocus = (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onFocus) {
      evt.persist()
      onFocus(evt)
    }
  }

  const handleBlur = (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onBlur) {
      evt.persist()
      onBlur(evt)
    }
  }

  const handleKeyDown = (evt: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (open) return
    const validKeys = [" ", "ArrowUp", "ArrowDown", "Enter"]
    if (validKeys.indexOf(evt.key) !== -1) {
      evt.preventDefault()
      setOpen(true)
    }
  }

  const handleDown = (_evt: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    setOpen(true)
  }

  const onClose = () => {
    setRanges(reset())
    setOpen(false)
  }

  const handleSubmit = (
    _evt: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    onRangeChange(ranges[0].startDate, ranges[0].endDate)
    setOpen(false)
  }

  const handleClear = () => {
    onRangeChange(null, null)
    if (onRangeClear) {
      setRanges(reset())
      onRangeClear()
    }
    setOpen(false)
  }

  return (
    <div
      className={cx(
        styles.root,
        {
          [styles.fullWidth]: fullWidth,
          [styles.disabled]: disabled,
        },
        styles.variants[variant],
        className,
      )}
    >
      <div ref={pairRef} data-disabled={disabled}>
        <TextField
          size={size}
          variant="outlined"
          margin={margin}
          disabled={disabled}
          value={inputValue}
          placeholder={placeholder}
          label={label}
          name="date-range"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPointerDown={handleDown}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className={cx(styles.input)}
          InputProps={{
            startAdornment: hasStartAdornment ? (
              <CalendarIcon className={styles.calendarIcon} />
            ) : undefined,
            classes: {
              root: inputClassName,
              notchedOutline: cx(
                { [styles.focused]: open && !error },
                { [styles.focusedError]: open && !!error },
                notchedClasName,
              ),
              adornedStart: styles.startAdornment,
              input: styles.outlinedInput,
            },
          }}
          InputLabelProps={{
            shrink: open || !!inputValue,
            classes: {
              shrink: cx({ [styles.labelShrink]: open }),
              outlined: cx(
                { [styles.label]: !open && !inputValue },
                { [labelClassName || ""]: !open && !inputValue },
                { [styles.labelBlurShrink]: !!inputValue },
              ),
            },
          }}
          inputProps={{
            readOnly: true,
          }}
          helperText={error ? t(error) : ""}
          error={!!error}
        />
      </div>

      <Popover
        keepMounted={true}
        open={open}
        onClose={onClose}
        anchorEl={pairRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div
          id="date_range"
          className={css`
            display: flex;
            flex-direction: column;
            width: 100%;
            overflow: hidden;
          `}
        >
          <ReactDateRange
            ranges={ranges}
            onChange={handleChange}
            months={2}
            weekStartsOn={1}
            direction="horizontal"
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            editableDateInputs={false}
            showPreview={true}
            showDateDisplay={false}
            rangeColors={["#00b1a3"]}
            minDate={parseDate(minDate || min_date, timeZone, dateLocale)}
            maxDate={parseDate(maxDate || max_date, timeZone, dateLocale)}
            className={css`
              .rdrDayToday .rdrDayNumber span {
                font-weight: 300;
                :after {
                  background: transparent;
                }
              }
            `}
          />
          <div
            className={css`
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              padding: 24px 1rem 24px 0.5px;
              background-color: var(--lighter-grey);
            `}
          >
            <ButtonGroup>
              <Button color="primary" variant="outlined" onClick={handleClear}>
                {clearLabel}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
              >
                {submitLabel}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Popover>
    </div>
  )
}

/*






  OOOOOOOOOOOOOOOOOO





*/

export type DateRangeButtonTriggerProps = {
  className?: string
  //
  disabled?: boolean
  start?: Date | number | string | null
  end?: Date | number | string | null
  onRangeChange: (start: Date | null, end: Date | null) => void
  onRangeClear?: () => void
  //
  minDate?: Date | number | string | null
  maxDate?: Date | number | string | null
  //
  name?: string
  placeholder?: string
  //
  label?: string
  submitLabel?: string
  clearLabel?: string
  error?: string
  //
  hasStartAdornment?: boolean
  hasFocusedDate?: boolean
  asTextButton?: boolean
}
export function DateRangeButtonTrigger(props: DateRangeButtonTriggerProps) {
  const {
    disabled = false,
    start,
    end,
    onRangeChange,
    //
    onRangeClear,
    //
    minDate,
    maxDate,

    placeholder = "",
    //
    submitLabel = "apply",
    clearLabel = "cancel",
    //
    hasStartAdornment,
    hasFocusedDate,
    asTextButton,
    className,
  } = props
  const { dateLocale } = useI18n()
  const { meta, dateFormatter } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}

  const date_now = utcToZonedTime(new Date(), timeZone)
  min_date.setFullYear(date_now.getFullYear() - DATE_RANGE_YEARS_PAST)
  max_date.setFullYear(date_now.getFullYear() + DATE_RANGE_YEARS_FUTURE)

  const pairRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState<boolean>(() => false)
  // added a key to cause the date picker to rerender every time the range
  // changes or gets cleared
  const [key, setKey] = useState<boolean>(true)

  const _start =
    typeof start !== "string" && typeof start !== "number"
      ? removeLocaleTzOffset(start)?.toISOString()
      : start
  const _end =
    typeof end !== "string" && typeof end !== "number"
      ? removeLocaleTzOffset(end)?.toISOString()
      : end
  const startDate = parseDate(_start, timeZone, dateLocale)
  const startInputValue = dateFormatter(_start, range_display_format_obj) || ""

  const endDate = parseDate(_end, timeZone, dateLocale)
  const endInputValue = dateFormatter(_end, range_display_format_obj) || ""
  const displayValue =
    start && end ? startInputValue + " - " + endInputValue : placeholder

  const currentRange = useMemo(
    () => [
      {
        key: "selection",
        startDate,
        endDate,
      },
    ],
    //eslint-disable-next-line
    [start, end],
  )

  const handleChange = (item: any) => {
    onRangeChange(item.selection?.startDate, item.selection?.endDate)
  }

  const handleKeyDown = (evt: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (open) return
    const validKeys = [" ", "ArrowUp", "ArrowDown", "Enter"]
    if (validKeys.indexOf(evt.key) !== -1) {
      evt.preventDefault()
      setOpen(true)
    }
  }

  const handleDown = (_evt: PointerEvent<HTMLButtonElement>) => {
    if (disabled) return
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const handleSubmit = () => setOpen(false)

  const handleClear = () => {
    onRangeChange(null, null)
    if (onRangeClear) {
      onRangeClear()
      setKey(!key)
    }
    setOpen(false)
  }

  return (
    <>
      <div ref={pairRef}>
        <button
          data-testid="date-range"
          disabled={disabled}
          onPointerDown={handleDown}
          onKeyDown={handleKeyDown}
          className={cx(
            css`
              position: relative;
              display: inline-flex;
              box-sizing: border-box;
              font-family: inherit;
              white-space: nowrap;
              vertical-align: middle;
              cursor: pointer;
              user-select: none;
              appearance: none;
              text-decoration: none;
              outline: none;
              text-align: center;
              align-items: center;

              background: transparent;
              border-radius: 4px;

              font-size: var(--font-size, 1.125rem);
              font-weight: var(--font-weight, normal);
              padding: 4px;

              border-color: transparent;
              border-width: 1px;
              border-style: solid;

              transition:
                background-color,
                border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

              &:enabled:hover {
                border-color: var(--primary-main, #00b1a4);
                .icon {
                  color: var(--black, #000);
                }
              }
              &:focus {
                border-color: var(--primary-main, #00b1a4);
                color: var(--primary-main, #00b1a4);
                .icon {
                  color: var(--primary-main, #00b1a4);
                }
              }

              .icon {
                color: var(--dark-grey, #808080);
              }

              &:disabled .icon {
                opacity: 0.6;
              }

              .icon-start {
                margin-inline-start: -4px;
                margin-inline-end: 4px;
              }
            `,
            asTextButton &&
              css`
                font-size: 0.8125rem;
                font-weight: 400;
                color: var(--black);

                &:enabled:hover {
                  border-color: transparent;
                  .icon {
                    color: var(--black, #000);
                  }
                }
                &:focus {
                  border-color: transparent;
                  color: var(--primary-main, #00b1a4);
                  .icon {
                    color: var(--primary-main, #00b1a4);
                  }
                }
              `,
            open &&
              css`
                color: var(--primary-main, #00b1a4);
                .icon {
                  color: var(--primary-main, #00b1a4);
                }
              `,
            className,
          )}
        >
          {hasStartAdornment ? (
            <span className="icon icon-start">
              <IconCalendar />
            </span>
          ) : null}
          {displayValue}
        </button>
      </div>

      <Popover
        keepMounted={true}
        open={open}
        onClose={onClose}
        anchorEl={pairRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div
          id="date_range"
          className={css`
            display: flex;
            flex-direction: column;
            width: 100%;
            overflow: hidden;
          `}
        >
          <ReactDateRange
            key={key.toString()}
            ranges={currentRange}
            onChange={handleChange}
            months={2}
            weekStartsOn={1}
            direction="horizontal"
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            editableDateInputs={false}
            showPreview={true}
            showDateDisplay={false}
            rangeColors={["#00b1a3"]}
            shownDate={
              hasFocusedDate
                ? startOfDay(utcToZonedTime(minDate || min_date, timeZone))
                : null
            }
            minDate={startOfDay(utcToZonedTime(minDate || min_date, timeZone))}
            maxDate={endOfDay(utcToZonedTime(maxDate || max_date, timeZone))}
            className={css`
              .rdrDayToday .rdrDayNumber span {
                font-weight: 300;
                :after {
                  background: transparent;
                }
              }
            `}
          />
          <div
            className={css`
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              padding: 24px 1rem 24px 0.5px;
              background-color: var(--lighter-grey);
            `}
          >
            <ButtonGroup>
              <Button color="primary" variant="outlined" onClick={handleClear}>
                {clearLabel}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
              >
                {submitLabel}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Popover>
    </>
  )
}

export type LocalDateRangeButtonTriggerProps = {
  className?: string
  //
  disabled?: boolean
  start?: Date | number | string | null
  end?: Date | number | string | null
  onRangeChange: (start: Date | null, end: Date | null) => void
  onRangeClear?: () => void
  //
  minDate?: Date | number | string | null
  maxDate?: Date | number | string | null
  //
  name?: string
  placeholder?: string
  //
  label?: string
  submitLabel?: string
  clearLabel?: string
  error?: string
  //
  hasStartAdornment?: boolean
  asTextButton?: boolean
}
export function LocalDateRangeButtonTrigger(
  props: LocalDateRangeButtonTriggerProps,
) {
  const {
    disabled = false,
    start,
    end,
    onRangeChange,
    //
    onRangeClear,
    //
    minDate,
    maxDate,

    placeholder = "",
    //
    submitLabel = "apply",
    clearLabel = "cancel",
    //
    hasStartAdornment,
    asTextButton,
    className,
  } = props
  const { dateLocale } = useI18n()

  const date_now = new Date()
  min_date.setFullYear(date_now.getFullYear() - DATE_RANGE_YEARS_PAST)
  max_date.setFullYear(date_now.getFullYear() + DATE_RANGE_YEARS_FUTURE)

  const pairRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState<boolean>(() => false)

  const _start =
    typeof start !== "string" && typeof start !== "number"
      ? start?.toISOString()
      : start
  const _end =
    typeof end !== "string" && typeof end !== "number"
      ? end?.toISOString()
      : end
  const startDate = _start ? addLocaleTzOffset(new Date(_start)) : null
  const startInputValue =
    startDate?.toLocaleString(dateLocale.code, range_display_format_obj) ?? ""

  const endDate = _end ? addLocaleTzOffset(new Date(_end)) : null
  const endInputValue =
    endDate?.toLocaleString(dateLocale.code, range_display_format_obj) ?? ""

  const displayValue =
    start && end ? startInputValue + " - " + endInputValue : placeholder

  const currentRange = useMemo(
    () => [
      {
        key: "selection",
        startDate: startDate || date_now,
        endDate: endDate || date_now,
      },
    ],
    //eslint-disable-next-line
    [start, end],
  )

  const handleChange = (item: any) => {
    onRangeChange(item.selection?.startDate, item.selection?.endDate)
  }

  const handleKeyDown = (evt: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (open) return
    const validKeys = [" ", "ArrowUp", "ArrowDown", "Enter"]
    if (validKeys.indexOf(evt.key) !== -1) {
      evt.preventDefault()
      setOpen(true)
    }
  }

  const handleDown = (_evt: PointerEvent<HTMLButtonElement>) => {
    if (disabled) return
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const handleSubmit = () => setOpen(false)

  const handleClear = () => {
    onRangeChange(null, null)
    if (onRangeClear) {
      onRangeClear()
    }
    setOpen(false)
  }

  return (
    <>
      <div ref={pairRef}>
        <button
          data-testid="date-range"
          disabled={disabled}
          onPointerDown={handleDown}
          onKeyDown={handleKeyDown}
          className={cx(
            css`
              position: relative;
              display: inline-flex;
              box-sizing: border-box;
              font-family: inherit;
              white-space: nowrap;
              vertical-align: middle;
              cursor: pointer;
              user-select: none;
              appearance: none;
              text-decoration: none;
              outline: none;
              text-align: center;
              align-items: center;

              background: transparent;
              border-radius: 4px;

              font-size: var(--font-size, 1.125rem);
              font-weight: var(--font-weight, normal);
              padding: 4px;

              border-color: transparent;
              border-width: 1px;
              border-style: solid;

              transition:
                background-color,
                border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

              &:enabled:hover {
                border-color: var(--primary-main, #00b1a4);
                .icon {
                  color: var(--black, #000);
                }
              }
              &:focus {
                border-color: var(--primary-main, #00b1a4);
              }

              .icon {
                color: var(--dark-grey, #808080);
              }

              &:disabled .icon {
                opacity: 0.6;
              }

              .icon-start {
                margin-inline-start: -4px;
                margin-inline-end: 4px;
              }
            `,
            asTextButton &&
              css`
                font-size: 0.8125rem;
                font-weight: 400;
                color: var(--black);

                &:enabled:hover {
                  border-color: transparent;
                  .icon {
                    color: var(--black, #000);
                  }
                }
                &:focus {
                  border-color: transparent;
                  color: var(--primary-main, #00b1a4);
                  .icon {
                    color: var(--primary-main, #00b1a4);
                  }
                }
              `,
            open &&
              css`
                color: var(--primary-main, #00b1a4);
                .icon {
                  color: var(--primary-main, #00b1a4);
                }
              `,
            className,
          )}
        >
          {hasStartAdornment ? (
            <span className="icon icon-start">
              <IconCalendar />
            </span>
          ) : null}
          {displayValue}
        </button>
      </div>

      <Popover
        keepMounted={true}
        open={open}
        onClose={onClose}
        anchorEl={pairRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div
          id="date_range"
          className={css`
            display: flex;
            flex-direction: column;
            width: 100%;
            overflow: hidden;
          `}
        >
          <ReactDateRange
            ranges={currentRange}
            onChange={handleChange}
            months={2}
            weekStartsOn={1}
            direction="horizontal"
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            editableDateInputs={false}
            showPreview={true}
            showDateDisplay={false}
            rangeColors={["#00b1a3"]}
            minDate={startOfDay(
              (typeof minDate === "string" ? new Date(minDate) : minDate) ||
                min_date,
            )}
            maxDate={endOfDay(
              (typeof maxDate === "string" ? new Date(maxDate) : maxDate) ||
                max_date,
            )}
            className={css`
              .rdrDayToday .rdrDayNumber span {
                font-weight: 300;
                :after {
                  background: transparent;
                }
              }
            `}
          />
          <div
            className={css`
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              padding: 24px 1rem 24px 0.5px;
              background-color: var(--lighter-grey);
            `}
          >
            <ButtonGroup>
              <Button color="primary" variant="outlined" onClick={handleClear}>
                {clearLabel}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
              >
                {submitLabel}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Popover>
    </>
  )
}
